// src/components/Poster.js
import React, { useRef, useEffect, useState } from 'react';
import { Image as KonvaImage, Text, Group, Rect } from 'react-konva';
import useImage from 'use-image';
import { ReactBar } from '../reactBar';
import { PosterReactParticle } from './PosterReactParticle';

const Poster = React.memo(({
  src,
  id,
  x,
  y,
  reaction_count_1,
  reaction_count_2,
  reaction_count_3,
  reaction_count_4,
  fastenerSrc,
  onPosterTouchStart,
  onPosterTouchEnd,
  zIndex,
  lastTouchedPosterId,
  onPosterLoaded,
  latitude,
  longitude,
  eventUrl
}) => {
  const [image, status] = useImage(src);
  const [fastenerImage] = useImage(fastenerSrc);
  const [dimensions, setDimensions] = useState({ width: 250, height: 350 });
  const [showReactBar, setShowReactBar] = useState(false);
  const [selectedReaction, setSelectedReaction] = useState([0, 0, 0, 0]);
  const [particles, setParticles] = useState([]);

  const isLongPressRef = useRef(false);
  const groupRef = useRef();
  const reactBarGroupRef = useRef();

  const fastenerWidth = 50;
  const fastenerHeight = 50;

  const rotationRef = useRef(Math.random() * 14 - 7);

  useEffect(() => {
    if (image) {
      const maxWidth = 350;
      const maxHeight = 350;
      let newWidth = image.width;
      let newHeight = image.height;

      // Calculate the scaling factor
      const scale = Math.min(
        maxWidth / image.width,
        maxHeight / image.height,
        1 // This ensures we only scale down, never up
      );

      // Apply the scaling
      newWidth = image.width * scale;
      newHeight = image.height * scale;

      setDimensions({ width: newWidth, height: newHeight });

      if (status === "loaded") {
        onPosterLoaded();
      }
    }
  }, [image, status, onPosterLoaded]);

  useEffect(() => {
    if (zIndex && groupRef.current) {
      groupRef.current.zIndex(zIndex);
      if (reactBarGroupRef.current) {
        reactBarGroupRef.current.zIndex(zIndex + 1);
      }
    }
  }, [zIndex, showReactBar]);

  useEffect(() => {
    if (lastTouchedPosterId !== id) {
      setShowReactBar(false);
    }
  }, [lastTouchedPosterId, id]);

  useEffect(() => {
    if (status === 'failed') {
      console.error(`Failed to load image from source: ${src}`);
    }
  }, [status, src]);

  return (
    <>
      <Group
        x={x}
        y={y}
        ref={groupRef}
        onMouseDown={() => onPosterTouchStart(id, setShowReactBar, latitude, longitude)}
        onMouseUp={() => onPosterTouchEnd(isLongPressRef, setShowReactBar)}
        onTouchStart={() => onPosterTouchStart(id, setShowReactBar, latitude, longitude)}
        onTouchEnd={() => onPosterTouchEnd(isLongPressRef, setShowReactBar)}
        rotation={rotationRef.current}>
        {status === 'failed' ? (
          <Text text="Image failed to load." fontSize={16} fill="red" />
        ) : (
          <>
            <Rect
              width={dimensions.width + 4}
              height={dimensions.height + 4}
              x={-2}
              y={-2}
              shadowColor="rgba(0,0,0,0.3)"
              shadowBlur={3}
              shadowOffset={{ x: 2, y: 2 }}
              shadowOpacity={1}
              fill="white"
              cornerRadius={2}
            />
            <KonvaImage
              image={image}
              width={dimensions.width}
              height={dimensions.height}
              draggable={false}
            />
            <KonvaImage
              image={fastenerImage}
              width={fastenerWidth}
              height={fastenerHeight}
              x={(dimensions.width - fastenerWidth) / 2}
              y={-fastenerHeight / 2}
              draggable={false}
            />
          </>
        )}
        {particles.map(particle => (
          <PosterReactParticle
            key={particle.id}
            letter={particle.letter}
            startX={particle.startX}
            startY={particle.startY}
          />
        ))}
      </Group>
      {showReactBar && (
        <Group
          x={x}
          y={y - 50}
          rotation={rotationRef.current}
          ref={reactBarGroupRef}
        >
          <ReactBar
            posterWidth={dimensions.width}
            onSelectionChange={setSelectedReaction}
            initialSelection={selectedReaction}
            eventUrl={eventUrl}
            shareURL={`${window.location.origin}/?lat=${latitude}&long=${longitude}&posterId=${id}`}
            posterImageUrl={src}
            posterId={id}
            reaction_count_1={reaction_count_1}
            reaction_count_2={reaction_count_2}
            reaction_count_3={reaction_count_3}
            reaction_count_4={reaction_count_4}
          />
        </Group>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.src === nextProps.src &&
    prevProps.fastenerSrc === nextProps.fastenerSrc &&
    prevProps.x === nextProps.x &&
    prevProps.y === nextProps.y &&
    prevProps.zIndex === nextProps.zIndex &&
    prevProps.lastTouchedPosterId === nextProps.lastTouchedPosterId &&
    prevProps.latitude === nextProps.latitude &&
    prevProps.longitude === nextProps.longitude &&
    prevProps.eventUrl === nextProps.eventUrl &&
    prevProps.reaction_count_1 === nextProps.reaction_count_1 &&
    prevProps.reaction_count_2 === nextProps.reaction_count_2 &&
    prevProps.reaction_count_3 === nextProps.reaction_count_3 &&
    prevProps.reaction_count_4 === nextProps.reaction_count_4
  );
});

Poster.displayName = 'Poster';

export default Poster;
